import React from 'react';
import '../../../i18n/i18next';
import AdminApp from '../../../admin/components/AdminApp';
import {
  AgnosticGuiProvider,
  EditorMode,
  setFilterQueryAndArgs,
  setGetCellRenderer,
  setGetHeaderCellRenderer,
  setBelowTitleComponentFunc,
  setAdditionalAnnotationComponentFunc,
  setGetCustomActionColRenderer,
  setFilterSelectValuesFunc,
} from '@sdxcorp/sdx-dds-gui-lib';
import apolloClient from '../../../lib/apolloClient';
import ErrorBoundary from '../ErrorBoundry';

import {
  getCurrentDate,
  getNonBusinessDays,
  getFilterQueryAndArgs,
  getCustomCellRenderer,
  getCustomHeaderCellRenderer,
  getBelowTitleComponent,
  getAdditionalAnnotationComponent,
  getActionColRenderer,
  filterSelectValues,
  dynamicValues,
  renderCustomViewButton,
} from '../../../admin/extensions';
import ProcessingDate from '../../../base/components/molecules/ProcessingDate';
import { colsWidthWeight, customActionBtnConfig } from '../../../admin/extensions';
import { Paths } from '../../../admin/routing';
import { TabName } from '../../../admin/components/Pages/Transactions/SettlementInstructions/SettlementInstructions';
import { EntityRoutingConfig } from '@sdxcorp/sdx-dds-gui-lib/dist/lib/ddsGui/AgnosticGuiProvider';
import { isCryptoCurrencySelector } from '../../../admin/components/Pages/Transactions/CryptoDepositWithdrawal/crypto_utils';

const appRoot = '/admin';
const editorRoot = '/reference-data';
const transactionsRoot = '/transactions';
const operationsRoot = '/operations';
const entityRoutingConfig: EntityRoutingConfig[] = [
  {
    entityType: 'Member',
    routePathToType: '',
    routerEntityType: 'members',
  },
  {
    entityType: 'Account',
    routePathToType: '',
    routerEntityType: 'accounts',
  },
  {
    entityType: 'SettlementInstruction',
    acceptsSubType: (subType: string | undefined) => !isCryptoCurrencySelector(subType),
    routerEntityType: 'settlement-instructions',
    routePathToType: transactionsRoot,
    customEntityChanges: (id: string) =>
      Paths.settlementInstructions.details
        .replace('/:tab', `/${TabName.ALLEGEMENTS}`)
        .replace('/:subType?', '')
        .replace(':mode(view|edit|create|changes|from)', EditorMode.CHANGES.toLowerCase())
        .replace('/:id?', `/${id}`),
    customEntityDetails: (id?: string, mode: EditorMode = EditorMode.VIEW, subType?: string) =>
      Paths.settlementInstructions.details
        .replace('/:tab', `/${TabName.ALLEGEMENTS}`)
        .replace('/:subType?', subType ? `/${subType}` : '')
        .replace(':mode(view|edit|create|changes|from)', mode.toLowerCase())
        .replace('/:id?', id ? `/${id}` : ''),
  },
  {
    entityType: 'SettlementInstruction',
    acceptsSubType: (subType: string | undefined) => isCryptoCurrencySelector(subType),
    routerEntityType: 'deposit-withdrawal',
    routePathToType: transactionsRoot,
    customEntityChanges: (id: string) =>
      Paths.cryptoDepositWithdrawals.details
        .replace('/:tab', `/${TabName.ALLEGEMENTS}`)
        .replace('/:subType?', '')
        .replace(':mode(view|edit|create|changes|from)', EditorMode.CHANGES.toLowerCase())
        .replace('/:id?', `/${id}`),
    customEntityDetails: (id?: string, mode: EditorMode = EditorMode.VIEW, subType?: string) =>
      Paths.cryptoDepositWithdrawals.details
        .replace('/:tab', `/${TabName.ALLEGEMENTS}`)
        .replace('/:subType?', subType ? `/${subType}` : '')
        .replace(':mode(view|edit|create|changes|from)', mode.toLowerCase())
        .replace('/:id?', id ? `/${id}` : ''),
  },
  {
    entityType: 'PaymentInstruction',
    routerEntityType: 'payment-instructions',
    routePathToType: transactionsRoot,
    customEntityChanges: (id: string) =>
      Paths.paymentInstructions.details
        .replace('/:tab', `/${TabName.OPEN_INSTRUCTIONS}`)
        .replace('/:subType?', '')
        .replace(':mode(view|edit|create|changes|from)', EditorMode.CHANGES.toLowerCase())
        .replace('/:id?', `/${id}`),
    customEntityDetails: (id?: string, mode: EditorMode = EditorMode.VIEW, subType?: string) =>
      Paths.paymentInstructions.details
        .replace('/:tab', `/${TabName.OPEN_INSTRUCTIONS}`)
        .replace('/:subType?', subType ? `/${subType}` : '')
        .replace(':mode(view|edit|create|changes|from)', mode.toLowerCase())
        .replace('/:id?', id ? `/${id}` : ''),
  },
  {
    entityType: 'BusinessCalendar',
    routerEntityType: 'business-calendar',
    routePathToType: operationsRoot,
    customEntityChanges: (id: string) =>
      Paths.businessCalendar.details
        .replace('/:subType?', '')
        .replace(':mode(view|edit|create|changes)', EditorMode.CHANGES.toLowerCase())
        .replace('/:id?', `/${id}`),
  },
  {
    entityType: 'BusinessCalendarDay',
    routerEntityType: 'business-calendar-day',
    routePathToType: operationsRoot,
    customEntityChanges: (id: string) =>
      Paths.businessCalendarDay.details
        .replace('/:subType?', '')
        .replace(':mode(view|edit|create|changes)', EditorMode.CHANGES.toLowerCase())
        .replace('/:id?', `/${id}`),
  },
  {
    entityType: 'CorporateActionEvent',
    routerEntityType: 'corporate-actions',
    routePathToType: '',
    customEntityChanges: (id: string) =>
      Paths.corporateActions.details
        .replace('/:subType?', '')
        .replace(':mode(view|edit|create|changes)', EditorMode.CHANGES.toLowerCase())
        .replace('/:id?', `/${id}`),
  },
  {
    entityType: 'Asset',
    routerEntityType: 'assets',
    routePathToType: '',
    customEntityChanges: (id?: string, mode: EditorMode = EditorMode.VIEW, subType?: string) => {
      return Paths.assets.details
        .replace(':mode(view|edit|create|changes|from)', EditorMode.CHANGES.toLowerCase())
        .replace('/:subType?', subType ? `/${subType}` : '')
        .replace('/:id?', `/${id}`);
    },
  },
  {
    entityType: 'Currency',
    routerEntityType: 'currencies',
    routePathToType: '',
    customEntityChanges: (id: string) => Paths.currencies.changes.replace('/:id?', `/${id}`),
  },
];

setFilterQueryAndArgs(getFilterQueryAndArgs);
setGetCellRenderer(getCustomCellRenderer);
setGetHeaderCellRenderer(getCustomHeaderCellRenderer);
setBelowTitleComponentFunc(getBelowTitleComponent);
setAdditionalAnnotationComponentFunc(getAdditionalAnnotationComponent);
setGetCustomActionColRenderer(getActionColRenderer);
setFilterSelectValuesFunc(filterSelectValues);

export const App = () => {
  return (
    <ErrorBoundary>
      <AgnosticGuiProvider
        errorBoundry={ErrorBoundary}
        routing={{ appRoot, editorRoot, entityRoutingConfig }}
        graphql={{ apolloClient: apolloClient as never }}
        wrapper={AdminApp}
        ddsVersion="1.0.0"
        mainScrollEltCssSelector="#layout-content"
        renderCustomViewButton={renderCustomViewButton}
        overview={{
          aboveTableContainer: {
            leftSlot: ProcessingDate,
          },
          colsWidthWeight,
          customActionBtnConfig,
          nonSortableColumns: {
            Account: ['tagRefs.tag.key'],
          },
        }}
        calendar={{
          getCurrentProcessingDate: getCurrentDate,
          getNonBusinessDaysOf: getNonBusinessDays,
        }}
        details={{
          initialValues: {
            Asset: {
              E: {
                quantityType: 'UNIT',
                isNativeAsset: true,
              },
              D: {
                quantityType: 'FAMT',
                isNativeAsset: true,
                interestType: 'FIXED',
              },
            },
            SettlementInstruction: {
              'cc-withdrawal': {
                priority: 'MEDIUM_FEES_NET',
              },
            },
          },
          dynamicValues,
        }}
      />
    </ErrorBoundary>
  );
};

export default React.memo(App);
